// Row actions for kore's DataTable: one ⋯ menu per row in a trailing column
// pinned to the table's right edge, the way the vouchers admin's tables do it.
//
// kore's DataTable has no per-column pinning (nor a per-column className), so
// the pinning is arbitrary-variant CSS on the table root aimed at the last
// header/body cell. That leans on DataTable's markup — a <table> inside an
// overflow-auto scroller, one <td> per column — so if kore grows a real pin
// option, `pinnedActionsTable` should go in favour of it. Overlay rows (the
// empty/loading/error states) render a single colSpan <td>, which
// `:not([colspan])` keeps out of it.

import { Button, type DataTableColumn, DropdownMenu } from "@pej/kore/components";
import type { LucideIcon } from "lucide-react";
import { MoreHorizontal } from "lucide-react";
import { Fragment } from "react";

export interface RowAction {
    id: string;
    label: string;
    icon: LucideIcon;
    onSelect: () => void;
    /** Rendered red, and grouped last below a separator. */
    destructive?: boolean;
    disabled?: boolean;
}

/** The DataTable className that pins the last column (see the file header).
 *  The pinned cells need an opaque background of their own so scrolled
 *  content passes under them, which means restating the row hover fill. */
export const pinnedActionsTable = [
    "[&_thead_th:last-child]:sticky [&_thead_th:last-child]:right-0 [&_thead_th:last-child]:bg-kore-surface-tint",
    "[&_tbody_tr>td:last-child:not([colspan])]:sticky [&_tbody_tr>td:last-child:not([colspan])]:right-0",
    "[&_tbody_tr>td:last-child:not([colspan])]:bg-kore-surface [&_tbody_tr>td:last-child:not([colspan])]:px-3",
    "[&_tbody_tr:hover>td:last-child:not([colspan])]:bg-kore-accent-softer",
    "[&_thead_th:last-child]:shadow-[inset_1px_0_0_var(--kore-border-subtle)]",
    "[&_tbody_tr>td:last-child:not([colspan])]:shadow-[inset_1px_0_0_var(--kore-border-subtle)]",
].join(" ");

export function RowActionsMenu({ label, actions }: { label: string; actions: RowAction[] }) {
    const ordered = [...actions.filter((a) => !a.destructive), ...actions.filter((a) => a.destructive)];
    const firstDestructive = ordered.findIndex((a) => a.destructive);

    return (
        // Clicks and keys stop here so they never reach the row's own
        // onClick/Enter navigation. That includes the menu items: the content
        // is portalled, but React events still bubble through this element.
        // biome-ignore lint/a11y/noStaticElementInteractions: propagation guard only; the trigger and items handle input
        <span className="inline-flex" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <DropdownMenu>
                <DropdownMenu.Trigger render={<Button variant="ghost" size="icon" aria-label={label} />}>
                    <MoreHorizontal />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="w-48">
                    {ordered.map((action, i) => (
                        <Fragment key={action.id}>
                            {i === firstDestructive && i > 0 && <DropdownMenu.Separator />}
                            <DropdownMenu.Item
                                variant={action.destructive ? "destructive" : "default"}
                                disabled={action.disabled}
                                onClick={action.onSelect}
                            >
                                <action.icon className="size-4" />
                                {action.label}
                            </DropdownMenu.Item>
                        </Fragment>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu>
        </span>
    );
}

/** The trailing actions column; pair it with `pinnedActionsTable` on the
 *  DataTable, and keep it last in `columns`. */
export function rowActionsColumn<T>(label: (row: T) => string, actions: (row: T) => RowAction[]): DataTableColumn<T> {
    return {
        key: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "right",
        width: "56px",
        cell: (row) => <RowActionsMenu label={label(row)} actions={actions(row)} />,
    };
}
